import { findLast } from "lodash"

import { DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import {
  StudentSkillHistory,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import { type EnumValues } from "#customTypes/types"
import { getCurrentSemester } from "#utils/semesterHelpers"

import { CLASS_GROUPS } from "./classOptions"

export const EVAL_TYPE_BY_CLASS_GROUP = {
  [CLASS_GROUPS.CORE]: StudentSkillType.core,
  [CLASS_GROUPS.PAINTING]: StudentSkillType.core,
  [CLASS_GROUPS.PORTFOLIO]: StudentSkillType.core,
  [CLASS_GROUPS.DIGITAL_ART]: StudentSkillType.digital,
  [CLASS_GROUPS.SCULPTURE]: StudentSkillType.sculpture,
} as const

export const CORE_SKILL_LEVELS = {
  BG: "BG",
  "BG^": "BG^",
  P: "P",
  "P^": "P^",
  BB: "BB",
  "BB^": "BB^",
  B: "B",
  "B^": "B^",
  G: "G",
  "G^": "G^",
  L: "L",
  "L^": "L^",
  Y: "Y",
  "Y^": "Y^",
  O: "O",
  "O^": "O^",
  R: "R",
} as const

export const DIGITAL_SKILL_LEVELS = {
  RO: "RO",
  "RO^": "RO^",
  TA: "TA",
  "TA^": "TA^",
  UBB: "UBB",
  "UBB^": "UBB^",
  BUP: "BUP",
  "BUP^": "BUP^",
  HD: "HD",
  "HD^": "HD^",
  YUM: "YUM",
  "YUM^": "YUM^",
  PIJ: "PIJ",
  "PIJ^": "PIJ^",
  THT: "THT",
  "THT^": "THT^",
  HI: "HI",
} as const

export const SCULPTURE_SKILL_LEVELS = {
  ST: "ST",
  "ST^": "ST^",
  LA: "LA",
  "LA^": "LA^",
  GRB: "GRB",
  "GRB^": "GRB^",
  EB: "EB",
  "EB^": "EB^",
  MAT: "MAT",
  "MAT^": "MAT^",
  MEM: "MEM",
  "MEM^": "MEM^",
  LE: "LE",
  "LE^": "LE^",
  MA: "MA",
  "MA^": "MA^",
  CH: "CH",
} as const

export const ALL_SKILL_LEVELS = {
  [CLASS_GROUPS.CORE]: CORE_SKILL_LEVELS,
  [CLASS_GROUPS.DIGITAL_ART]: DIGITAL_SKILL_LEVELS,
  [CLASS_GROUPS.SCULPTURE]: SCULPTURE_SKILL_LEVELS,
  [CLASS_GROUPS.PAINTING]: CORE_SKILL_LEVELS,
  [CLASS_GROUPS.PORTFOLIO]: CORE_SKILL_LEVELS,
} as const

// Sculpture skill levels
// Strawberry (Pink).    Abbv: St
// Lavender (Purple).    Abbv:  La
// GrapeBerry (or Purpletato) (Purple-Blue)  Abbv: GrB
// EarlBlue (Blue). Eb
// Matcha (Green). Abbv:  Ma
// Mellowmint (Yellow-Green).  Abbv:  MeM
// Lemon (Yellow).  Abbv:  Le
// Mango (Orange). Abbv:  Ma
// Cherry (Red). Abbv:  Ch

export const SCULPTURE_SKILL_LEVEL_TAGS = {
  [SCULPTURE_SKILL_LEVELS.ST]: "St",
  [SCULPTURE_SKILL_LEVELS["ST^"]]: "St^",
  [SCULPTURE_SKILL_LEVELS.LA]: "La",
  [SCULPTURE_SKILL_LEVELS["LA^"]]: "La^",
  [SCULPTURE_SKILL_LEVELS.GRB]: "GrB",
  [SCULPTURE_SKILL_LEVELS["GRB^"]]: "GrB^",
  [SCULPTURE_SKILL_LEVELS.EB]: "Eb",
  [SCULPTURE_SKILL_LEVELS["EB^"]]: "Eb^",
  [SCULPTURE_SKILL_LEVELS.MAT]: "Ma",
  [SCULPTURE_SKILL_LEVELS["MAT^"]]: "Ma^",
  [SCULPTURE_SKILL_LEVELS.MEM]: "MeM",
  [SCULPTURE_SKILL_LEVELS["MEM^"]]: "MeM^",
  [SCULPTURE_SKILL_LEVELS.LE]: "Le",
  [SCULPTURE_SKILL_LEVELS["LE^"]]: "Le^",
  [SCULPTURE_SKILL_LEVELS.MA]: "Ma",
  [SCULPTURE_SKILL_LEVELS["MA^"]]: "Ma^",
  [SCULPTURE_SKILL_LEVELS.CH]: "Ch",
}

// Digital art skill levels
// Rose Milk. Abbv:  Ro
// Taro. Abbv:  Ta
// Ube Berry. Abbv:  UbB
// Butterfly Pea. Abv:  BuP
// Honeydew. Abbv: Hd
// Yuzu Mint:   YuM
// Pineapple Jelly:   PiJ
// Thai Tea:   ThT
// Hibiscus:  Hi

export const ALL_SKILL_LEVEL_LABELS = {
  // Core
  [CORE_SKILL_LEVELS.BG]: "Bubblegum",
  [CORE_SKILL_LEVELS["BG^"]]: "Bubblegum^",
  [CORE_SKILL_LEVELS.P]: "Purple",
  [CORE_SKILL_LEVELS["P^"]]: "Purple^",
  [CORE_SKILL_LEVELS.BB]: "Blueberry",
  [CORE_SKILL_LEVELS["BB^"]]: "Blueberry^",
  [CORE_SKILL_LEVELS.B]: "Blue",
  [CORE_SKILL_LEVELS["B^"]]: "Blue^",
  [CORE_SKILL_LEVELS.G]: "Green",
  [CORE_SKILL_LEVELS["G^"]]: "Green^",
  [CORE_SKILL_LEVELS.L]: "Lime",
  [CORE_SKILL_LEVELS["L^"]]: "Lime^",
  [CORE_SKILL_LEVELS.Y]: "Yellow",
  [CORE_SKILL_LEVELS["Y^"]]: "Yellow^",
  [CORE_SKILL_LEVELS.O]: "Orange",
  [CORE_SKILL_LEVELS["O^"]]: "Orange^",
  [CORE_SKILL_LEVELS.R]: "Red",

  // Digital v2
  [DIGITAL_SKILL_LEVELS.RO]: "Rose Milk",
  [DIGITAL_SKILL_LEVELS["RO^"]]: "Rose Milk^",
  [DIGITAL_SKILL_LEVELS.TA]: "Taro",
  [DIGITAL_SKILL_LEVELS["TA^"]]: "Taro^",
  [DIGITAL_SKILL_LEVELS.UBB]: "Ube Berry",
  [DIGITAL_SKILL_LEVELS["UBB^"]]: "Ube Berry^",
  [DIGITAL_SKILL_LEVELS.BUP]: "Butterfly Pea",
  [DIGITAL_SKILL_LEVELS["BUP^"]]: "Butterfly Pea^",
  [DIGITAL_SKILL_LEVELS.HD]: "Honeydew",
  [DIGITAL_SKILL_LEVELS["HD^"]]: "Honeydew^",
  [DIGITAL_SKILL_LEVELS.YUM]: "Yuzu Mint",
  [DIGITAL_SKILL_LEVELS["YUM^"]]: "Yuzu Mint^",
  [DIGITAL_SKILL_LEVELS.PIJ]: "Pineapple Jelly",
  [DIGITAL_SKILL_LEVELS["PIJ^"]]: "Pineapple Jelly^",
  [DIGITAL_SKILL_LEVELS.THT]: "Thai Tea",
  [DIGITAL_SKILL_LEVELS["THT^"]]: "Thai Tea^",
  [DIGITAL_SKILL_LEVELS.HI]: "Hibiscus",

  // Sculpture v2
  [SCULPTURE_SKILL_LEVELS.ST]: "Strawberry",
  [SCULPTURE_SKILL_LEVELS["ST^"]]: "Strawberry^",
  [SCULPTURE_SKILL_LEVELS.LA]: "Lavender",
  [SCULPTURE_SKILL_LEVELS["LA^"]]: "Lavender^",
  [SCULPTURE_SKILL_LEVELS.GRB]: "GrapeBerry",
  [SCULPTURE_SKILL_LEVELS["GRB^"]]: "GrapeBerry^",
  [SCULPTURE_SKILL_LEVELS.EB]: "EarlBlue",
  [SCULPTURE_SKILL_LEVELS["EB^"]]: "EarlBlue^",
  [SCULPTURE_SKILL_LEVELS.MAT]: "Matcha",
  [SCULPTURE_SKILL_LEVELS["MAT^"]]: "Matcha^",
  [SCULPTURE_SKILL_LEVELS.MEM]: "Mellowmint",
  [SCULPTURE_SKILL_LEVELS["MEM^"]]: "Mellowmint^",
  [SCULPTURE_SKILL_LEVELS.LE]: "Lemon",
  [SCULPTURE_SKILL_LEVELS["LE^"]]: "Lemon^",
  [SCULPTURE_SKILL_LEVELS.MA]: "Mango",
  [SCULPTURE_SKILL_LEVELS["MA^"]]: "Mango^",
  [SCULPTURE_SKILL_LEVELS.CH]: "Cherry",
} as const

export const ALL_SKILL_LEVEL_CUSTOMER_LABELS = {
  // Core
  [CORE_SKILL_LEVELS.BG]: "Bubblegum Cloud 1",
  [CORE_SKILL_LEVELS["BG^"]]: "Bubblegum Cloud 2",
  [CORE_SKILL_LEVELS.P]: "Purple Cloud 1",
  [CORE_SKILL_LEVELS["P^"]]: "Purple Cloud 2",
  [CORE_SKILL_LEVELS.BB]: "Blueberry Cloud 1",
  [CORE_SKILL_LEVELS["BB^"]]: "Blueberry Cloud 2",
  [CORE_SKILL_LEVELS.B]: "Blue Cloud 1",
  [CORE_SKILL_LEVELS["B^"]]: "Blue Cloud 2",
  [CORE_SKILL_LEVELS.G]: "Green Cloud1",
  [CORE_SKILL_LEVELS["G^"]]: "Green Cloud 2",
  [CORE_SKILL_LEVELS.L]: "Lime Cloud 1",
  [CORE_SKILL_LEVELS["L^"]]: "Lime Cloud 2",
  [CORE_SKILL_LEVELS.Y]: "Yellow Cloud 1",
  [CORE_SKILL_LEVELS["Y^"]]: "Yellow Cloud 2",
  [CORE_SKILL_LEVELS.O]: "Orange Cloud 1",
  [CORE_SKILL_LEVELS["O^"]]: "Orange Cloud 2",
  [CORE_SKILL_LEVELS.R]: "Red Cloud",

  // Digital v2
  [DIGITAL_SKILL_LEVELS.RO]: "Rose Milk 1",
  [DIGITAL_SKILL_LEVELS["RO^"]]: "Rose Milk 2",
  [DIGITAL_SKILL_LEVELS.TA]: "Taro 1",
  [DIGITAL_SKILL_LEVELS["TA^"]]: "Taro 2",
  [DIGITAL_SKILL_LEVELS.UBB]: "Ube Berry 1",
  [DIGITAL_SKILL_LEVELS["UBB^"]]: "Ube Berry 2",
  [DIGITAL_SKILL_LEVELS.BUP]: "Butterfly Pea 1",
  [DIGITAL_SKILL_LEVELS["BUP^"]]: "Butterfly Pea 2",
  [DIGITAL_SKILL_LEVELS.HD]: "Honeydew 1",
  [DIGITAL_SKILL_LEVELS["HD^"]]: "Honeydew 2",
  [DIGITAL_SKILL_LEVELS.YUM]: "Yuzu Mint 1",
  [DIGITAL_SKILL_LEVELS["YUM^"]]: "Yuzu Mint 2",
  [DIGITAL_SKILL_LEVELS.PIJ]: "Pineapple Jelly 1",
  [DIGITAL_SKILL_LEVELS["PIJ^"]]: "Pineapple Jelly 2",
  [DIGITAL_SKILL_LEVELS.THT]: "Thai Tea 1",
  [DIGITAL_SKILL_LEVELS["THT^"]]: "Thai Tea 2",
  [DIGITAL_SKILL_LEVELS.HI]: "Hibiscus",

  // Sculpture v2
  [SCULPTURE_SKILL_LEVELS.ST]: "Strawberry 1",
  [SCULPTURE_SKILL_LEVELS["ST^"]]: "Strawberry 2",
  [SCULPTURE_SKILL_LEVELS.LA]: "Lavender 1",
  [SCULPTURE_SKILL_LEVELS["LA^"]]: "Lavender 2",
  [SCULPTURE_SKILL_LEVELS.GRB]: "GrapeBerry 1",
  [SCULPTURE_SKILL_LEVELS["GRB^"]]: "GrapeBerry 2",
  [SCULPTURE_SKILL_LEVELS.EB]: "EarlBlue 1",
  [SCULPTURE_SKILL_LEVELS["EB^"]]: "EarlBlue 2",
  [SCULPTURE_SKILL_LEVELS.MAT]: "Matcha 1",
  [SCULPTURE_SKILL_LEVELS["MAT^"]]: "Matcha 2",
  [SCULPTURE_SKILL_LEVELS.MEM]: "Mellowmint 1",
  [SCULPTURE_SKILL_LEVELS["MEM^"]]: "Mellowmint 2",
  [SCULPTURE_SKILL_LEVELS.LE]: "Lemon 1",
  [SCULPTURE_SKILL_LEVELS["LE^"]]: "Lemon 2",
  [SCULPTURE_SKILL_LEVELS.MA]: "Mango 1",
  [SCULPTURE_SKILL_LEVELS["MA^"]]: "Mango 2",
  [SCULPTURE_SKILL_LEVELS.CH]: "Cherry",
} as const

export const CORE_SKILL_LEVEL_OPTIONS: DropdownOption[] = Object.keys(
  CORE_SKILL_LEVELS
).map((key) => ({
  key,
  label: ALL_SKILL_LEVEL_LABELS[key as keyof typeof CORE_SKILL_LEVELS],
  value: key,
}))

export const DIGITAL_SKILL_LEVEL_OPTIONS: DropdownOption[] = Object.keys(
  DIGITAL_SKILL_LEVELS
).map((key) => ({
  key,
  label: ALL_SKILL_LEVEL_LABELS[key as keyof typeof DIGITAL_SKILL_LEVELS],
  value: key,
}))

export const SCULPTURE_SKILL_LEVEL_OPTIONS: DropdownOption[] = Object.keys(
  SCULPTURE_SKILL_LEVELS
).map((key) => ({
  key,
  label: ALL_SKILL_LEVEL_LABELS[key as keyof typeof SCULPTURE_SKILL_LEVELS],
  value: key,
}))

export const CLASS_GROUP_TO_SKILL_TYPE = {
  [CLASS_GROUPS.CORE]: StudentSkillType.core,
  [CLASS_GROUPS.PAINTING]: StudentSkillType.core,
  [CLASS_GROUPS.PORTFOLIO]: StudentSkillType.core,
  [CLASS_GROUPS.DIGITAL_ART]: StudentSkillType.digital,
  [CLASS_GROUPS.SCULPTURE]: StudentSkillType.sculpture,
} as const

const DEFAULT_SKILL_LEVEL = {
  [StudentSkillType.core]: "",
  [StudentSkillType.digital]: "",
  [StudentSkillType.sculpture]: "",
} as const

/**
 * Gets the most recent skill level for a specific class group
 * Walks backwards through semesters to find the most recent value for the requested class group
 * @param skillHistory - The student's skill history object
 * @param classGroup - The class group to get skill level for (CORE, DIGITAL_ART, SCULPTURE, etc)
 * @param targetSemester - Optional semester to look up to (defaults to current semester)
 * @returns The most recent skill level for the class group, or NA/undefined if not found
 */
export const getStudentSkill = (
  skillHistory: StudentSkillHistory = {},
  studentSkillType: EnumValues<typeof StudentSkillType>,
  targetSemester: string = getCurrentSemester()
): CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel | "" => {
  const sortedSemesters = Object.keys(skillHistory).sort()

  // Walk backwards through semesters to find the most recent value
  const mostRecentSemester = findLast(sortedSemesters, (semester) => {
    // Skip semesters that are after the target semester
    if (semester > targetSemester) return false

    return !!skillHistory[semester][studentSkillType]
  })

  return mostRecentSemester &&
    skillHistory[mostRecentSemester][studentSkillType]
    ? skillHistory[mostRecentSemester][studentSkillType]
    : DEFAULT_SKILL_LEVEL[studentSkillType]
}

export type CoreSkillLevel = keyof typeof CORE_SKILL_LEVELS
export type DigitalSkillLevel = keyof typeof DIGITAL_SKILL_LEVELS
export type SculptureSkillLevel = keyof typeof SCULPTURE_SKILL_LEVELS
export type AllSkillLevels =
  | CoreSkillLevel
  | DigitalSkillLevel
  | SculptureSkillLevel
export type SkillLevelLabel = EnumValues<typeof ALL_SKILL_LEVEL_LABELS>
export type CustomerSkillLevelLabel = EnumValues<
  typeof ALL_SKILL_LEVEL_CUSTOMER_LABELS
>

const CORE_SKILL_LEVELS_ARRAY = Object.keys(CORE_SKILL_LEVELS)
const DIGITAL_SKILL_LEVELS_ARRAY = Object.keys(DIGITAL_SKILL_LEVELS).concat(
  Object.keys(DIGITAL_SKILL_LEVELS)
)
const SCULPTURE_SKILL_LEVELS_ARRAY = Object.keys(SCULPTURE_SKILL_LEVELS).concat(
  Object.keys(SCULPTURE_SKILL_LEVELS)
)

export const sortSkillLevels = (
  skillLevels: CoreSkillLevel[] | DigitalSkillLevel[] | SculptureSkillLevel[],
  classGroup: EnumValues<typeof CLASS_GROUPS> = CLASS_GROUPS.CORE
): CoreSkillLevel[] | DigitalSkillLevel[] | SculptureSkillLevel[] =>
  skillLevels.slice().sort((a, b) => {
    if (classGroup === CLASS_GROUPS.SCULPTURE) {
      return (
        SCULPTURE_SKILL_LEVELS_ARRAY.indexOf(a) -
        SCULPTURE_SKILL_LEVELS_ARRAY.indexOf(b)
      )
    }

    if (classGroup === CLASS_GROUPS.DIGITAL_ART) {
      return (
        DIGITAL_SKILL_LEVELS_ARRAY.indexOf(a) -
        DIGITAL_SKILL_LEVELS_ARRAY.indexOf(b)
      )
    }

    return (
      CORE_SKILL_LEVELS_ARRAY.indexOf(a) - CORE_SKILL_LEVELS_ARRAY.indexOf(b)
    )
  })
